import React, { useEffect, useContext, useState } from "react";
import { Link } from "react-router-dom";
import { loginUser } from "../actions/userLogin";
import { AlertContext, UserContext } from "../context/MainContext";
import { useNavigate } from "react-router-dom";
import ForgotPasswordModal from "../components/ForgotPasswordModal";
export default function Login(props) {
  const [credential, setCredential] = useState(null);
  const [err, setErr] = useState(null);
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [waitingMessage, setWaitingMessage] = useState(null);
  const { user, setUser } = useContext(UserContext);
  const { alert, setAlert } = useContext(AlertContext);
  const [success, setSuccess] = useState(false);
  const [justLoggedIn, setJustLoggedIn] = useState(false);
  const nav = useNavigate();
  useEffect(() => {
    if (err) {
      setAlert(["Login Failed", err, "failed"]);
    }
  }, [err, setAlert]);

  // Update alert when waiting message changes (for cold start feedback)
  useEffect(() => {
    if (waitingMessage) {
      setAlert([
        "Please Wait",
        `${waitingMessage}\n\nDo not close this window or navigate away.`,
        "waiting",
      ]);
    }
  }, [waitingMessage, setAlert]);

  const handleSubmit = () => {
    if (!credential || !password) {
      setErr("You must enter your email/username and password.");
    } else {
      setAlert([
        "Your request for logging in has been submitted.",
        "Once it has been processed you will receive an update.",
        "waiting",
      ]);
      setJustLoggedIn(true);
      loginUser(credential, password, setUser, setSuccess, setErr, {
        setWaitingMessage,
      });
    }
  };
  useEffect(() => {
    if (user && justLoggedIn) {
      setAlert([
        "Login Success!",
        "Congratulations your request was successful, you are now logged in.",
        "confirm",
      ]);
      nav("/");
    }
  }, [user, justLoggedIn, nav, setAlert]);

  return (
    <div className="min-h-screen flex items-center justify-center py-16 px-4">
      {showForgotPassword && (
        <ForgotPasswordModal setModal={setShowForgotPassword} />
      )}

      <div className="w-full max-w-4xl grid grid-cols-1 lg:grid-cols-2 shadow-2xl rounded-2xl overflow-hidden border border-slate-200">
        <div className="bg-gradient-to-br from-blue-700 via-blue-600 to-blue-500 text-white p-10 flex flex-col justify-between">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] text-blue-100 mb-4 text-center">
              Welcome home
            </p>
            <h1 className="text-4xl font-bold leading-tight">
              Sign in to myLlave
            </h1>
            <p className="mt-4 text-blue-100 text-sm leading-relaxed">
              If you have registered for an account, your request is under
              review. Once accepted you will receive an email with a
              confirmation of acceptance and you will be able to log in with the
              credentials you provided.
            </p>
          </div>
          <div className="mt-10 space-y-3">
            <p className="text-sm text-blue-100">Don't have an account?</p>
            <Link
              to="/register"
              className="inline-flex items-center justify-center w-full px-4 py-3 bg-white text-blue-700 font-semibold rounded-xl shadow hover:shadow-lg transition-all"
            >
              Create your account in a few steps
            </Link>
            <button
              onClick={() => setShowForgotPassword(true)}
              className="inline-flex items-center justify-center w-full px-4 py-3 bg-blue-900/60 text-white font-semibold rounded-xl border border-white/30 hover:bg-blue-900 transition-all"
            >
              Forgot password?
            </button>
          </div>
        </div>

        <div className="p-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">Login</h2>
              <p className="text-sm text-slate-500">
                Enter your credentials to continue
              </p>
            </div>
            {err && <p className="text-sm text-red-500 font-medium">{err}</p>}
          </div>

          <div className="space-y-6">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-slate-700">
                Email Address or Username
              </label>
              <input
                className="bg-slate-100 px-4 py-3 rounded-lg border border-slate-200 focus:border-blue-500 focus:bg-white focus:outline-none"
                type="text"
                placeholder="you@example.com or your_username"
                onChange={(e) => setCredential(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-slate-700">
                Password
              </label>
              <div className="relative overflow-visible">
                <input
                  name="password"
                  type={passwordVisible ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="bg-slate-100 px-4 py-3 pr-16 w-full rounded-lg border border-slate-200 focus:border-blue-500 focus:bg-white focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setPasswordVisible((v) => !v)}
                  aria-label={
                    passwordVisible ? "Hide password" : "Show password"
                  }
                  className="absolute inset-y-0 right-2 flex items-center text-gray-600 hover:text-gray-900 p-2 z-20 bg-transparent focus:outline-none"
                >
                  {passwordVisible ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-5.523 0-10-5-10-7s4.477-7 10-7c1.043 0 2.045.164 2.999.47M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M3 3l18 18"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <button
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg shadow-lg shadow-blue-200 transition-all"
                onClick={() => handleSubmit()}
              >
                Login
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
